import { useCallback, useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import {
  artistRegistrationCompleteEvent,
  fillingRegisterFormEvent,
  locationEvent,
} from 'src/Helpers/TagManager'
import { signupLoginError } from 'src/Helpers/TagManager/AuthEvents'
import { getLoading } from 'src/Redux/auth-process/credentialsSlice/selectors'
import {
  registerArtist,
  RegisterArtistError,
} from 'src/Redux/auth-process/credentialsSlice/api-actions'
import { setAccessToken } from 'src/Redux/auth-process/credentialsSlice'
import { getCookieByName, getUtmParameters } from 'src/Constants/functions'
import { useSourceBusterEvent } from 'src/Hooks/useSourceBusterEvent'
import {
  AdCampaignStrategyEnum,
  CreateAdCampaignBody,
  useCreateAdCampaignMutation,
} from 'src/Redux/ad-campaignApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { createdFirst } from 'src/Router/routes'
import { ArtistRolesEnum } from 'src/Constants/enums'
import { initiateGoogleAuth } from 'src/Utils/googleAuth'
import { PendingCampaignData } from 'src/Utils/pendingCampaignData'
import { persistPendingReferralCodes } from 'src/Utils/pendingReferralCodes'
import { FormDataProps } from 'src/Components/ArtistContactDetailsForm'

import { useViewSignup } from '../ArtistRegisterPage/useViewSignup'

export interface UseMetaAdsRegisterPage {
  isEmailExist: boolean
  clearEmailExist: () => void
  handleSubmit: (data: FormDataProps, token: string) => Promise<void>
  onGoogleClick: () => void
  loading?: boolean
  onStartFillingForm: () => void
}

const HTTP_STATUS_CONFLICT = 409

type RouteParams = {
  strategy: AdCampaignStrategyEnum
  resourceId: string
}

type MetaAdsResourceData = {
  name: string
  ownerName: string
  coverUrl: string
  resourceSpotifyUrl: string
  ownerSpotifyUrl: string
}

const getCreateAdCampaignBody = (
  resourceData: MetaAdsResourceData,
  strategy: AdCampaignStrategyEnum,
  resourceId: string,
): CreateAdCampaignBody => ({
  strategy,
  resourceName: resourceData.name,
  resourceSpotifyId: resourceId,
  coverUrl: resourceData.coverUrl,
  ownerName: resourceData.ownerName,
  resourceSpotifyUrl: resourceData.resourceSpotifyUrl,
  ownerSpotifyUrl: resourceData.ownerSpotifyUrl,
})

const getPendingAdCampaign = (
  resourceData: MetaAdsResourceData | null,
  strategy: AdCampaignStrategyEnum | undefined,
  resourceId: string | undefined,
): CreateAdCampaignBody | null => {
  if (!resourceData || !strategy || !resourceId) {
    return null
  }
  return getCreateAdCampaignBody(resourceData, strategy, resourceId)
}

const buildCampaignsListUrlWithToken = (token: string): string => {
  const params = new URLSearchParams({ sc_token: token })
  return `${ArtistRoutesEnum.CampaignsList}?${params.toString()}`
}

export const useMetaAdsRegisterPage = (
  resourceData: MetaAdsResourceData | null,
): UseMetaAdsRegisterPage => {
  const navigate = useNavigate()
  const { search } = useCustomSearchParams()
  const { strategy, resourceId } = useParams<RouteParams>()
  const artistSignupLoading = useAppSelector(getLoading)

  const [isEmailExist, setIsEmailExist] = useState(false)
  const [createAdCampaign] = useCreateAdCampaignMutation()
  const { sendCreateFirstCampaignSourceBusterEvent } = useSourceBusterEvent()

  const dispatch = useAppDispatch()

  useViewSignup()

  const onStartFillingForm = useCallback((): void => {
    locationEvent()
    fillingRegisterFormEvent()
  }, [])

  const createPendingAdCampaign = useCallback(
    async (token: string): Promise<void> => {
      const pendingCampaign = getPendingAdCampaign(
        resourceData,
        strategy,
        resourceId,
      )

      if (!pendingCampaign) {
        // eslint-disable-next-line no-console
        console.error('Missing required data for Meta Ads campaign creation:', {
          hasResourceData: Boolean(resourceData),
          strategy,
          resourceId,
        })
        navigate(buildCampaignsListUrlWithToken(token))
        return
      }

      await createAdCampaign(pendingCampaign)
        .unwrap()
        .then((campaign) => {
          sendCreateFirstCampaignSourceBusterEvent(
            campaign.id,
            CampaignPlatformTypeEnum.META,
            token,
          )
          const params = new URLSearchParams({
            campaignId: String(campaign.id),
            sc_token: token,
          })
          navigate(
            `${createdFirst}/${CampaignPlatformTypeEnum.META}?${params.toString()}`,
          )
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Meta Ads campaign creation failed:', error)
          navigate(buildCampaignsListUrlWithToken(token))
        })
    },
    [
      navigate,
      resourceData,
      strategy,
      resourceId,
      createAdCampaign,
      sendCreateFirstCampaignSourceBusterEvent,
    ],
  )

  const handleRegisterArtistError = useCallback(
    (error: RegisterArtistError): void => {
      signupLoginError()

      if (error.status === HTTP_STATUS_CONFLICT) {
        setIsEmailExist(true)
      }
    },
    [],
  )

  const handleSubmit = useCallback(
    async (data: FormDataProps, token: string): Promise<void> => {
      setIsEmailExist(false)

      const utmObj = getUtmParameters(getCookieByName('soundCampaignUtm'))
      persistPendingReferralCodes({
        internalReferralCode: search?.get('referralCode') || void 0,
        referralCode: utmObj.ref || void 0,
      })

      await dispatch(
        registerArtist({
          artistRole: ArtistRolesEnum.ARTIST,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phoneNumber || void 0,
          captchaToken: token,
        }),
      )
        .unwrap()
        .then(async (res) => {
          dispatch(setAccessToken({ token: res.token }))

          if (res.userId) {
            artistRegistrationCompleteEvent({
              platformName: 'meta_ads',
              userId: res.userId,
              userRole: ArtistRolesEnum.ARTIST,
            })
          }

          await createPendingAdCampaign(res.token)
        })
        .catch(handleRegisterArtistError)
    },
    [dispatch, search, createPendingAdCampaign, handleRegisterArtistError],
  )

  const onGoogleClick = useCallback((): void => {
    const utmObj = getUtmParameters(getCookieByName('soundCampaignUtm'))
    const pendingCampaign = getPendingAdCampaign(
      resourceData,
      strategy,
      resourceId,
    )
    const campaignData: PendingCampaignData | undefined = pendingCampaign
      ? { platform: 'meta', body: pendingCampaign }
      : void 0

    initiateGoogleAuth({
      campaignData,
      internalReferralCode: search?.get('referralCode') || void 0,
      referralCode: utmObj.ref || void 0,
      isSignUp: true,
    })
  }, [resourceData, strategy, resourceId, search])

  const clearEmailExist = useCallback((): void => {
    setIsEmailExist(false)
  }, [])

  return {
    isEmailExist,
    clearEmailExist,
    handleSubmit,
    onGoogleClick,
    onStartFillingForm,
    loading: artistSignupLoading,
  }
}
